import { collection, doc, getCountFromServer, getDoc, type Firestore } from 'firebase/firestore'
import { i18n } from '@/config/i18n'

/**
 * Plan de la compañía: `Companies/{id}.plan`. Solo existen dos. Sin el campo
 * (toda compañía nueva) la compañía está en Free Premium.
 */
export type CompanyPlan = 'client' | 'freePremium'
export const CLIENT_PLAN: CompanyPlan = 'client'

/** A quién se escribe para cambiar de plan (Plan y Facturación). */
export const PLAN_CONTACT_EMAIL = 'angelica.duque@kila.co'

/** Embarques que puede crear una compañía Free Premium en total. Borrar uno no libera cupo. */
export const FREE_PREMIUM_SHIPMENT_LIMIT = 3

export function resolveCompanyPlan(company: { plan?: unknown } | null | undefined): CompanyPlan {
  return company?.plan === CLIENT_PLAN ? 'client' : 'freePremium'
}

/** IA = la compañía tiene al menos una acción en aiPermissions o el Asistente IA encendido. */
export function companyHasAI(company: { aiPermissions?: unknown; showAiAgent?: unknown } | null | undefined): boolean {
  return (Array.isArray(company?.aiPermissions) && company.aiPermissions.length > 0) || company?.showAiAgent === true
}

/**
 * `GLOBAL_CONFIG.shipmentCounter` es el siguiente número SHIP- a asignar: arranca
 * en 1, lo suben el front y las CF de tracking y nunca baja al borrar. Los
 * embarques creados en la historia de la compañía son counter - 1.
 */
export function shipmentsCreatedFromCounter(counter: unknown): number {
  return typeof counter === 'number' && counter > 1 ? counter - 1 : 0
}

export function canCreateShipments(plan: CompanyPlan, used: number, count = 1): boolean {
  return plan === CLIENT_PLAN || used + count <= FREE_PREMIUM_SHIPMENT_LIMIT
}

/** El mensaje va traducido: OC, correo, cotización y Warehouse muestran err.message tal cual. */
export class ShipmentLimitReachedError extends Error {
  constructor(public companyId: string) {
    super(i18n.global.t('dashboard.billing.limit.message', { max: FREE_PREMIUM_SHIPMENT_LIMIT }))
    this.name = 'ShipmentLimitReachedError'
  }
}

/**
 * Embarques usados por una compañía Free Premium. Es el mayor entre el contador
 * y los embarques vivos: hay compañías con más embarques que contador (caminos
 * viejos que crearon sin subirlo), y con más de 3 quedan bloqueadas igual.
 */
export async function getShipmentsUsed(db: Firestore, companyId: string): Promise<number> {
  const [configSnap, liveCount] = await Promise.all([
    getDoc(doc(db, 'Companies', companyId, 'config', 'GLOBAL_CONFIG')),
    getCountFromServer(collection(db, 'Companies', companyId, 'shipments'))
  ])
  return Math.max(shipmentsCreatedFromCounter(configSnap.data()?.shipmentCounter), liveCount.data().count)
}

/** Para los caminos que crean el embarque en una CF (tracking, OC, correo, cotización). */
export async function assertCanCreateShipments(db: Firestore, companyId: string, count = 1): Promise<void> {
  const companySnap = await getDoc(doc(db, 'Companies', companyId))
  const plan = resolveCompanyPlan(companySnap.data())
  if (plan === CLIENT_PLAN) return
  if (!canCreateShipments(plan, await getShipmentsUsed(db, companyId), count)) {
    throw new ShipmentLimitReachedError(companyId)
  }
}
